import { useEffect, useMemo } from 'react';
import { useParams } from 'next/navigation';
import { Product } from 'shared/types/product/Product';
import { mapProduct } from 'helpers/algolia/map-product';
import { parseSkuCodes, productsInSkuOrder } from 'helpers/algolia/product-ids';
import type { ProductTileLayout } from 'components/commercetools-ui/organisms/product/product-tile';

export type ProductBlockType = 'Algolia Query' | 'Product IDs';

export const PRODUCT_BLOCK_TYPES: readonly ProductBlockType[] = ['Algolia Query', 'Product IDs'];

/**
 * The fields `section_with_product_slider` and `section_with_product_grid` share — the two blocks differ
 * only in their type key (`slider_type` / `grid_type`) and their layout.
 */
export interface ProductBlockContent {
  title?: string;
  /** Rich text — even one plain line arrives wrapped in `<p>`, so it is sanitized and set as HTML. */
  description?: string;
  /** 'Algolia Query': an Algolia `filters` string; `${localeKey}` → en-AU / en-NZ. */
  algolia_query?: string;
  /** 'Product IDs': comma-separated variant item-codes, shown in that order (e.g. "ABC123, DEF456"). */
  product_ids?: string;
  additional_css?: string;
  additional_css_class_names?: string;
  /**
   * Attached server-side by helpers/algolia/preload-cms-product-blocks: the hits on success, `[]` when
   * the backend gave up after its Algolia retries, absent when the CMS entry gave nothing to query.
   */
  __ssrHits?: any[];
}

/**
 * The products a V2 product slider / grid renders, and the tile layout for its type (SERVER PATH — the
 * hits were preloaded, the browser never queries Algolia for a block):
 *   - 'Algolia Query' → index order (new first), `grid` tiles;
 *   - 'Product IDs'   → the author's order with the listed variant selected, `compact` tiles.
 *
 * With nothing preloaded (blank CMS query / list) the block can only show its error message, so the cause
 * is named in the BROWSER console rather than leaving an unexplained error strip on the page.
 */
export const useProductBlock = (
  kind: 'slider' | 'grid',
  type: ProductBlockType,
  content: ProductBlockContent,
): { products: Product[]; tileLayout: ProductTileLayout } => {
  const { locale } = useParams();
  const hits = content?.__ssrHits;
  const preloaded = Array.isArray(hits);
  const queryField = type === 'Product IDs' ? 'product_ids' : 'algolia_query';
  const queryValue = content?.[queryField];
  const title = content?.title;

  useEffect(() => {
    if (preloaded) return;
    console.error(
      `%c[Algolia PLP] ${kind} NOT loaded — no Algolia query was run`,
      'color:#dc2626;font-weight:bold',
      [
        '',
        `  reason:   the CMS ${queryField} for this ${kind} is BLANK`,
        `  section:  ${title || '(untitled)'}`,
        `  type:     ${type}`,
        `  value:    ${JSON.stringify(queryValue ?? null)}`,
      ].join('\n'),
    );
  }, [preloaded, kind, queryField, queryValue, title, type]);

  const productIds = content?.product_ids;
  const products = useMemo(() => {
    if (!hits) return [];
    return type === 'Product IDs'
      ? productsInSkuOrder(hits, parseSkuCodes(productIds), locale as string)
      : hits.map((hit) => mapProduct(hit, locale as string));
  }, [hits, type, productIds, locale]);

  return { products, tileLayout: type === 'Product IDs' ? 'compact' : 'grid' };
};
